import { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/base/SeoHead';
import { useTheme } from '@/context/ThemeContext';
import { getBackendConfigError } from '@/lib/api/client';
import { registerDealer } from '@/lib/api/profiles';

function DealerApplyPageContent() {
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const { theme } = useTheme();
  const configError = getBackendConfigError();
  const light = theme === 'light';

  const bg = light ? 'bg-gray-50' : 'bg-[#0D1B2A]';
  const heroOverlay = light ? 'opacity-[0.06]' : 'opacity-10';
  const headingColor = light ? 'text-gray-900' : 'text-white';
  const bodyColor = light ? 'text-gray-600' : 'text-white/65';
  const mutedColor = light ? 'text-gray-500' : 'text-white/55';
  const faintColor = light ? 'text-gray-400' : 'text-white/35';
  const infoCard = light
    ? 'bg-white/85 border border-gray-200 shadow-sm shadow-gray-200/60'
    : 'bg-white/5 border border-white/10';
  const formCard = light
    ? 'bg-white border border-gray-200 shadow-2xl shadow-gray-200/70'
    : 'bg-[#1C2B3A] border border-white/10 shadow-2xl shadow-black/20';
  const labelColor = light ? 'text-gray-500' : 'text-white/60';
  const inputBg = light
    ? 'bg-gray-50 border border-gray-200 text-gray-900 placeholder-gray-400 focus:border-[#F97316]'
    : 'bg-[#0D1B2A] border border-white/10 text-white placeholder-white/25 focus:border-[#F97316]';
  const secondaryButton = light
    ? 'border border-gray-200 text-gray-600 hover:text-gray-900 hover:border-gray-300 hover:bg-gray-50'
    : 'border border-white/15 text-white/75 hover:text-white hover:border-white/30';

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    setSuccessMessage('');

    const form = event.currentTarget;
    const motivation = (form.elements.namedItem('motivation') as HTMLTextAreaElement).value.trim();

    if (motivation.length < 30) {
      setError('Please share a bit more about your business and why you want to represent Stingray.');
      return;
    }

    setLoading(true);

    try {
      await registerDealer({
        firstName: (form.elements.namedItem('first_name') as HTMLInputElement).value.trim(),
        lastName: (form.elements.namedItem('last_name') as HTMLInputElement).value.trim(),
        companyName: (form.elements.namedItem('company_name') as HTMLInputElement).value.trim(),
        email: (form.elements.namedItem('email') as HTMLInputElement).value.trim(),
        phone: (form.elements.namedItem('phone') as HTMLInputElement).value.trim(),
        country: (form.elements.namedItem('country') as HTMLInputElement).value.trim(),
        website: (form.elements.namedItem('website') as HTMLInputElement).value.trim(),
        businessFocus: (form.elements.namedItem('business_focus') as HTMLSelectElement).value,
        motivation,
        state: '',
        address: '',
      });

      setSubmitted(true);
      setSuccessMessage('Application received. Our team will review it and contact you shortly.');
      form.reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to submit your application.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${bg} transition-colors duration-300`}>
      <SeoHead
        title="Become a Dealer | Stingray"
        description="Apply to join the Stingray dealer network."
      />
      <Navbar />

      <section className="relative overflow-hidden px-6 pt-28 pb-20">
        <div className={`absolute inset-0 ${heroOverlay}`}>
          <img
            src="https://readdy.ai/api/search-image?query=deep%20navy%20ocean%20surface%20abstract%20marine%20texture%20premium%20dark%20blue%20background%20subtle%20waves%20cinematic%20professional%20website%20backdrop&width=1600&height=1200&seq=dealer-apply-bg&orientation=portrait"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto grid gap-8 lg:grid-cols-[0.95fr_1.05fr] items-start">
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 bg-[#F97316]/15 border border-[#F97316]/30 rounded-full px-4 py-2 text-[#F97316] text-xs font-bold tracking-[0.3em] uppercase">
              <i className="ri-compass-3-line"></i>
              Dealer Application
            </div>

            <div>
              <h1 className={`text-4xl md:text-5xl font-black leading-tight mb-4 ${headingColor}`}>
                Let&apos;s build the next great Stingray territory.
              </h1>
              <p className={`${bodyColor} text-base md:text-lg max-w-xl leading-relaxed`}>
                Share a few essentials about your company, your market, and why Stingray is a fit.
                We kept this short on purpose so serious partners can apply in a few minutes.
              </p>
            </div>

            <div className="grid gap-4">
              {[
                {
                  title: 'Who you are',
                  body: 'Company details, direct contact info, and where you operate.',
                  icon: 'ri-briefcase-4-line',
                },
                {
                  title: 'How you sell',
                  body: 'Dealer, distributor, service center, boat builder, or e-commerce focus.',
                  icon: 'ri-ship-line',
                },
                {
                  title: 'Why Stingray',
                  body: 'A short note on your customers, market fit, and partnership motivation.',
                  icon: 'ri-star-smile-line',
                },
              ].map((item) => (
                <div key={item.title} className={`${infoCard} rounded-2xl p-5 transition-colors duration-300`}>
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-[#F97316]/15 border border-[#F97316]/25 flex items-center justify-center shrink-0">
                      <i className={`${item.icon} text-[#F97316] text-lg`}></i>
                    </div>
                    <div>
                      <h2 className={`${headingColor} font-bold mb-1`}>{item.title}</h2>
                      <p className={`${mutedColor} text-sm leading-relaxed`}>{item.body}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className={`${faintColor} text-xs`}>
              Already approved?{' '}
              <Link to="/dealer" className="text-[#F97316] hover:underline">
                Sign in to the dealer portal
              </Link>
            </p>
          </div>

          <div className={`${formCard} rounded-[28px] p-6 md:p-8 transition-colors duration-300`}>
            {submitted ? (
              <div className="text-center py-10">
                <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/20 flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-green-400"></i>
                </div>
                <h2 className={`${headingColor} text-2xl font-black mb-3`}>Application submitted</h2>
                <p className={`${mutedColor} text-sm leading-relaxed max-w-md mx-auto mb-6`}>
                  {successMessage}
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/dealer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-bold text-white whitespace-nowrap"
                    style={{ backgroundColor: 'var(--accent)' }}
                  >
                    <i className="ri-login-box-line"></i>
                    Dealer Portal
                  </Link>
                  <button
                    type="button"
                    onClick={() => {
                      setSubmitted(false);
                      setSuccessMessage('');
                    }}
                    className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${secondaryButton}`}
                  >
                    <i className="ri-refresh-line"></i>
                    Submit another
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <h2 className={`${headingColor} text-2xl font-black mb-2`}>Become a Stingray dealer</h2>
                  <p className={`${mutedColor} text-sm`}>
                    A short application for marine partners, resellers, and regional distributors.
                  </p>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>First Name</label>
                    <input
                      type="text"
                      name="first_name"
                      required
                      placeholder="John"
                      className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                    />
                  </div>
                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Last Name</label>
                    <input
                      type="text"
                      name="last_name"
                      required
                      placeholder="Smith"
                      className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    required
                    placeholder="Marine Supply Co."
                    className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                  />
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Work Email</label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="dealer@company.com"
                      className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                    />
                  </div>
                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="+1 (555) 000-0000"
                      className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                    />
                  </div>
                </div>

                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Country / Region</label>
                    <input
                      type="text"
                      name="country"
                      required
                      placeholder="United States"
                      className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                    />
                  </div>
                  <div>
                    <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Website or Social</label>
                    <input
                      type="text"
                      name="website"
                      placeholder="company.com"
                      className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                    />
                  </div>
                </div>

                <div>
                  <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>Business Focus</label>
                  <select
                    name="business_focus"
                    required
                    className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors ${inputBg}`}
                  >
                    <option value="">Select your primary channel</option>
                    <option value="Retail dealer">Retail dealer</option>
                    <option value="Regional distributor">Regional distributor</option>
                    <option value="Boat builder or OEM">Boat builder or OEM</option>
                    <option value="Marine service center">Marine service center</option>
                    <option value="E-commerce seller">E-commerce seller</option>
                    <option value="Other marine partner">Other marine partner</option>
                  </select>
                </div>

                <div>
                  <label className={`block ${labelColor} text-xs font-semibold uppercase tracking-wide mb-2`}>
                    Tell us about your business and why you want to carry Stingray
                  </label>
                  <textarea
                    name="motivation"
                    required
                    rows={6}
                    placeholder="A few lines is enough. Tell us what you sell today, who your customers are, what region you cover, and why Stingray is a strong fit."
                    className={`w-full text-sm px-4 py-3 rounded-xl focus:outline-none transition-colors resize-none ${inputBg}`}
                  />
                </div>

                {configError && (
                  <p className="text-amber-300 text-xs bg-amber-500/10 border border-amber-500/20 rounded-xl px-3 py-2.5">
                    {configError}
                  </p>
                )}
                {error && (
                  <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2.5">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={loading || Boolean(configError)}
                  className="w-full inline-flex items-center justify-center gap-2 bg-[#F97316] text-white font-bold py-3.5 rounded-xl hover:bg-[#ea6a0a] transition-colors cursor-pointer whitespace-nowrap text-sm uppercase tracking-wide disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  <i className={loading ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'}></i>
                  {loading ? 'Submitting...' : 'Submit Dealer Application'}
                </button>

                <p className={`${faintColor} text-xs leading-relaxed`}>
                  We review applications manually. If the fit is right, we&apos;ll follow up and activate your dealer portal access.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default function DealerApplyPage() {
  return <DealerApplyPageContent />;
}
